// ============================================
// pages/admin/GunnySuppliersManagement.tsx — SUPER_ADMIN only
// ============================================
import React, { useState, useEffect } from 'react';
import { Plus, Edit3, Trash2, CheckCircle, AlertCircle, Package, IndianRupee, KeyRound, Copy, X, Loader2, RotateCcw } from 'lucide-react';
import { gunnySuppliersAPI, districtsAPI, importAPI, gunnySupplierRatesAPI, usersAPI } from '../../api/services';
import { MasterImportBar } from '../../components/MasterImportBar';
import { BAG_TYPE_OPTIONS, bagTypeLabel } from '../../components/gunny/bagTypes';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types/markfed';

interface District {
  id: number;
  name: string;
}

interface GunnySupplier {
  id: number;
  supplier_name: string;
  tender_id: string;
  district_id: number;
  district_name?: string;
  district?: { name: string };
  emd_amount: number;
  emd_utr: string;
  security_deposit: number;
  security_deposit_utr: string;
  is_active: boolean;
}

export const GunnySuppliersManagement: React.FC = () => {
  const { hasRole } = useAuth();
  const canProvision = hasRole(UserRole.DM, UserRole.MANAGER_PROCUREMENT, UserRole.AO_CAO, UserRole.SUPER_ADMIN);
  const [suppliers, setSuppliers] = useState<GunnySupplier[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [provisioning, setProvisioning] = useState<number | null>(null);
  const [credModal, setCredModal] = useState<{ name: string; email: string; password: string } | null>(null);

  const handleProvisionLogin = async (s: GunnySupplier) => {
    if (!window.confirm(`Create a login for "${s.supplier_name}"?\n\nA temp password will be shown once — copy it before closing the dialog.`)) return;
    setProvisioning(s.id);
    setMessage(null);
    try {
      const { data } = await usersAPI.provisionSupplier(s.id);
      setCredModal({ name: s.supplier_name, email: data.user.email, password: data.temp_password });
    } catch (err: any) {
      if (err?.response?.status === 409) {
        const offer = window.confirm(
          `${err.response.data?.message || 'A login already exists for this supplier.'}\n\nReset the password now?`,
        );
        if (offer) {
          try {
            const { data } = await usersAPI.resetSupplierPassword(s.id);
            setCredModal({ name: s.supplier_name, email: data.user.email, password: data.temp_password });
          } catch (err2: any) {
            setMessage({ type: 'error', text: err2?.response?.data?.message || 'Failed to reset password' });
          }
        }
      } else {
        setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to provision login' });
      }
    } finally {
      setProvisioning(null);
    }
  };

  const handleResetPassword = async (s: GunnySupplier) => {
    if (!window.confirm(`Reset password for "${s.supplier_name}"?\n\nThe existing password stops working immediately. The new temp password will be shown once.`)) return;
    setProvisioning(s.id);
    setMessage(null);
    try {
      const { data } = await usersAPI.resetSupplierPassword(s.id);
      setCredModal({ name: s.supplier_name, email: data.user.email, password: data.temp_password });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to reset password' });
    } finally {
      setProvisioning(null);
    }
  };

  const copyToClipboard = async (text: string) => {
    try { await navigator.clipboard.writeText(text); } catch { /* clipboard blocked */ }
  };
  const [form, setForm] = useState({
    supplier_name: '',
    tender_id: '',
    district_id: 0,
    emd_amount: 0,
    emd_utr: '',
    security_deposit: 0,
    security_deposit_utr: '',
    is_active: true,
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loading, setLoading] = useState(true);

  // Rate-sheet sub-modal: opened from a supplier row, manages all rates for
  // that supplier × bag-type. Backed by the gunny_supplier_rates master.
  const [ratesFor, setRatesFor] = useState<GunnySupplier | null>(null);
  const [rates, setRates] = useState<any[]>([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [newRate, setNewRate] = useState<{ bag_type: string; rate_per_bag: number }>({ bag_type: BAG_TYPE_OPTIONS[0].value, rate_per_bag: 0 });

  const openRates = async (s: GunnySupplier) => {
    setRatesFor(s);
    setRatesLoading(true);
    try {
      const { data } = await gunnySupplierRatesAPI.list(s.id);
      setRates(Array.isArray(data) ? data : []);
      setNewRate({ bag_type: BAG_TYPE_OPTIONS[0].value, rate_per_bag: 0 });
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.message || 'Failed to load rates' });
    } finally { setRatesLoading(false); }
  };

  const addRate = async () => {
    if (!ratesFor) return;
    if (!(newRate.rate_per_bag > 0)) { setMessage({ type: 'error', text: 'Rate must be > 0' }); return; }
    try {
      await gunnySupplierRatesAPI.create({
        supplier_id: ratesFor.id,
        bag_type: newRate.bag_type,
        rate_per_bag: Number(newRate.rate_per_bag),
      });
      const { data } = await gunnySupplierRatesAPI.list(ratesFor.id);
      setRates(data || []);
      setNewRate({ bag_type: BAG_TYPE_OPTIONS[0].value, rate_per_bag: 0 });
      setMessage({ type: 'success', text: 'Rate added' });
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.message || 'Add rate failed' });
    }
  };

  const updateRate = async (id: number, patch: { rate_per_bag?: number; is_active?: boolean }) => {
    if (!ratesFor) return;
    try {
      await gunnySupplierRatesAPI.update(id, patch);
      const { data } = await gunnySupplierRatesAPI.list(ratesFor.id);
      setRates(data || []);
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.message || 'Update failed' });
    }
  };

  const deleteRate = async (id: number) => {
    if (!ratesFor) return;
    if (!window.confirm('Delete this rate? Bundling will fail until you add it back.')) return;
    try {
      await gunnySupplierRatesAPI.delete(id);
      setRates((rs) => rs.filter((r) => r.id !== id));
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.response?.data?.message || 'Delete failed' });
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [suppliersRes, districtsRes] = await Promise.all([
          gunnySuppliersAPI.list(),
          districtsAPI.list(),
        ]);
        setSuppliers(Array.isArray(suppliersRes.data) ? suppliersRes.data : (suppliersRes.data as any)?.data || []);
        setDistricts(Array.isArray(districtsRes.data) ? districtsRes.data : (districtsRes.data as any)?.data || []);
      } catch { setMessage({ type: 'error', text: 'Failed to load data' }); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const getDistrictName = (s: GunnySupplier) => {
    if (s.district_name) return s.district_name;
    if (s.district?.name) return s.district.name;
    const found = districts.find((d) => d.id === s.district_id);
    return found?.name || '-';
  };

  const openAdd = () => {
    setEditingId(null);
    setForm({ supplier_name: '', tender_id: '', district_id: 0, emd_amount: 0, emd_utr: '', security_deposit: 0, security_deposit_utr: '', is_active: true });
    setShowModal(true);
  };

  const openEdit = (s: GunnySupplier) => {
    setEditingId(s.id);
    setForm({
      supplier_name: s.supplier_name || '',
      tender_id: s.tender_id || '',
      district_id: s.district_id || 0,
      emd_amount: s.emd_amount || 0,
      emd_utr: s.emd_utr || '',
      security_deposit: s.security_deposit || 0,
      security_deposit_utr: s.security_deposit_utr || '',
      is_active: s.is_active,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.supplier_name.trim()) { setMessage({ type: 'error', text: 'Supplier name is required' }); return; }
    try {
      const payload = { ...form, district_id: form.district_id || undefined };
      if (editingId) {
        const { data } = await gunnySuppliersAPI.update(editingId, payload);
        setSuppliers(suppliers.map((s) => (s.id === editingId ? data : s)));
      } else {
        const { data } = await gunnySuppliersAPI.create(payload);
        setSuppliers([...suppliers, data]);
      }
      setShowModal(false);
      setMessage({ type: 'success', text: editingId ? 'Supplier updated' : 'Supplier created' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) { setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to save' }); }
  };

  const formatCurrency = (val: number) => {
    if (!val) return '-';
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val);
  };

  const handleDelete = async (s: GunnySupplier) => {
    if (!window.confirm(`Delete supplier "${s.supplier_name}"?`)) return;
    try {
      await gunnySuppliersAPI.delete(s.id);
      setSuppliers(suppliers.filter((x) => x.id !== s.id));
      setMessage({ type: 'success', text: 'Supplier deleted' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err: any) { setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to delete' }); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3"><Package className="w-7 h-7 text-blue-600" />Gunny Suppliers Management</h1>
          <p className="text-sm text-gray-500 mt-1">Manage gunny bag supplier master data</p>
        </div>
        <div className="flex items-center gap-2">
          <MasterImportBar
            label="Import Suppliers"
            templatePath="/import/gunny-suppliers/template"
            uploader={(f) => importAPI.gunnySuppliers(f)}
            onImported={() => gunnySuppliersAPI.list().then(({ data }) => setSuppliers(Array.isArray(data) ? data : (data as any)?.data || []))}
            onMessage={setMessage}
          />
          <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg shadow-lg shadow-blue-500/30"><Plus className="w-4 h-4" />Add Supplier</button>
        </div>
      </div>

      {message && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}{message.text}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead><tr className="bg-gray-50 border-b">
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Supplier Name</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">Tender ID</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-600">District</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-600">EMD Amount</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-600">Security Deposit</th>
              <th className="px-4 py-3 text-center font-semibold text-gray-600">Active</th>
              <th className="px-4 py-3 text-center font-semibold text-gray-600 w-20">Actions</th>
            </tr></thead>
            <tbody>
              {suppliers.map((s) => (
                <tr key={s.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{s.supplier_name}</td>
                  <td className="px-4 py-3 font-mono text-gray-600">{s.tender_id || '-'}</td>
                  <td className="px-4 py-3">{getDistrictName(s)}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatCurrency(s.emd_amount)}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatCurrency(s.security_deposit)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${s.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{s.is_active ? 'Yes' : 'No'}</span>
                  </td>
                  <td className="px-4 py-3 text-center flex items-center justify-center gap-1">
                    <button onClick={() => openRates(s)} title="Rates" className="p-1.5 text-emerald-700 hover:bg-emerald-50 rounded"><IndianRupee className="w-4 h-4" /></button>
                    <button onClick={() => openEdit(s)} title="Edit" className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"><Edit3 className="w-4 h-4" /></button>
                    {canProvision && s.is_active && (
                      <>
                        <button
                          onClick={() => handleProvisionLogin(s)}
                          disabled={provisioning === s.id}
                          title="Create supplier login"
                          className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded disabled:opacity-50"
                        >
                          {provisioning === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleResetPassword(s)}
                          disabled={provisioning === s.id}
                          title="Reset supplier password"
                          className="p-1.5 text-amber-600 hover:bg-amber-50 rounded disabled:opacity-50"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    <button onClick={() => handleDelete(s)} title="Deactivate" className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {suppliers.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No suppliers found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {credModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold flex items-center gap-2"><KeyRound className="w-5 h-5 text-emerald-600" />Login created</h2>
                <button onClick={() => setCredModal(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Share these credentials with <span className="font-semibold">{credModal.name}</span>. The user will be forced to change the password on first login. <span className="font-semibold text-amber-700">This is the only time the password will be shown.</span>
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Email (login id)</label>
                  <div className="flex items-stretch gap-2">
                    <input readOnly value={credModal.email} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 font-mono" />
                    <button onClick={() => copyToClipboard(credModal.email)} className="px-3 py-2 text-emerald-700 border border-emerald-200 hover:bg-emerald-50 rounded-lg" title="Copy"><Copy className="w-4 h-4" /></button>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Temporary password</label>
                  <div className="flex items-stretch gap-2">
                    <input readOnly value={credModal.password} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 font-mono" />
                    <button onClick={() => copyToClipboard(credModal.password)} className="px-3 py-2 text-emerald-700 border border-emerald-200 hover:bg-emerald-50 rounded-lg" title="Copy"><Copy className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button onClick={() => setCredModal(null)} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold">Done</button>
              </div>
            </div>
          </div>
        </>
      )}

      {showModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowModal(false)} />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-lg font-bold mb-4">{editingId ? 'Edit Supplier' : 'Add Supplier'}</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Name</label>
                  <input type="text" value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="e.g. XYZ Jute Industries" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tender ID</label>
                    <input type="text" value={form.tender_id} onChange={(e) => setForm({ ...form, tender_id: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="e.g. MKFD/GUNNY/2025-01" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
                    <select value={form.district_id} onChange={(e) => setForm({ ...form, district_id: Number(e.target.value) })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                      <option value={0}>-- Select --</option>
                      {districts.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">EMD Amount</label>
                    <input type="number" value={form.emd_amount || ''} onChange={(e) => setForm({ ...form, emd_amount: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">EMD UTR</label>
                    <input type="text" value={form.emd_utr} onChange={(e) => setForm({ ...form, emd_utr: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="UTR reference" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Security Deposit</label>
                    <input type="number" value={form.security_deposit || ''} onChange={(e) => setForm({ ...form, security_deposit: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Security Deposit UTR</label>
                    <input type="text" value={form.security_deposit_utr} onChange={(e) => setForm({ ...form, security_deposit_utr: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="UTR reference" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <label className="block text-sm font-medium text-gray-700">Active</label>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, is_active: !form.is_active })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.is_active ? 'bg-blue-600' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
                <button onClick={handleSave} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">{editingId ? 'Update' : 'Create'}</button>
              </div>
            </div>
          </div>
        </>
      )}

      {ratesFor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="font-semibold text-gray-900">Rate sheet — {ratesFor.supplier_name}</h3>
              <p className="text-xs text-gray-500 mt-0.5">One rate per bag type. Bundling reads from this list.</p>
            </div>

            <div className="px-6 py-4">
              {ratesLoading ? (
                <div className="text-center py-8 text-gray-500 text-sm">Loading…</div>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr className="text-left text-xs uppercase text-gray-600">
                        <th className="px-3 py-2">Bag type</th>
                        <th className="px-3 py-2 text-right w-40">Rate (₹)</th>
                        <th className="px-3 py-2 text-center w-24">Active</th>
                        <th className="px-3 py-2 w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rates.map((r) => (
                        <tr key={r.id} className="border-b last:border-0">
                          <td className="px-3 py-2 text-gray-800">{bagTypeLabel(r.bag_type)}</td>
                          <td className="px-3 py-2">
                            <input type="number" step="0.01" min={0}
                              defaultValue={Number(r.rate_per_bag)}
                              onBlur={(e) => {
                                const v = Number(e.target.value);
                                if (v > 0 && v !== Number(r.rate_per_bag)) updateRate(r.id, { rate_per_bag: v });
                              }}
                              className="w-full px-2 py-1 border rounded text-right tabular-nums" />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <input type="checkbox" checked={!!r.is_active}
                              onChange={(e) => updateRate(r.id, { is_active: e.target.checked })} />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button onClick={() => deleteRate(r.id)} className="p-1 text-rose-600 hover:bg-rose-50 rounded">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {rates.length === 0 && (
                        <tr><td colSpan={4} className="px-3 py-6 text-center text-sm text-gray-400">No rates yet — add the first one below.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Add rate</div>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="flex-1 min-w-[180px]">
                    <label className="block text-xs text-gray-600 mb-1">Bag type</label>
                    <select value={newRate.bag_type}
                      onChange={(e) => setNewRate({ ...newRate, bag_type: e.target.value })}
                      className="w-full px-3 py-2 border rounded text-sm bg-white">
                      {BAG_TYPE_OPTIONS.filter((o) => !rates.some((r) => r.bag_type === o.value)).map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="w-40">
                    <label className="block text-xs text-gray-600 mb-1">Rate (₹)</label>
                    <input type="number" step="0.01" min={0} value={newRate.rate_per_bag || ''}
                      onChange={(e) => setNewRate({ ...newRate, rate_per_bag: Number(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border rounded text-sm text-right tabular-nums" />
                  </div>
                  <button onClick={addRate}
                    disabled={!(newRate.rate_per_bag > 0) || rates.some((r) => r.bag_type === newRate.bag_type)}
                    className="px-3 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50">
                    Add
                  </button>
                </div>
                {BAG_TYPE_OPTIONS.every((o) => rates.some((r) => r.bag_type === o.value)) && (
                  <p className="mt-2 text-xs text-gray-500">All bag types have rates configured.</p>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
              <button onClick={() => { setRatesFor(null); setRates([]); }}
                className="px-4 py-2 text-sm bg-gray-700 hover:bg-gray-800 text-white rounded">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GunnySuppliersManagement;
